import React, { useCallback, useRef, useState } from 'react';
import { registerComponent } from '../../lib/vulcan-lib/components';
import { AnalyticsContext, useTracking } from '@/lib/analyticsEvents';
import { isFriendlyUI, preferredHeadingCase } from '@/themes/forumTheme';
import Button from '@/lib/vendor/@material-ui/core/src/Button';
import ForumIcon from '../common/ForumIcon';
import LWPopper from '../common/LWPopper';
import LWClickAwayListener from '../common/LWClickAwayListener';
import DropdownMenu from '../dropdowns/DropdownMenu';
import DropdownItem from '../dropdowns/DropdownItem';
import { Paper } from '../widgets/Paper';
import { useCurrentUser } from '../common/withUser';
import { useDialog } from '../common/withDialog';
import LoginPopup from '../users/LoginPopup';

const styles = (theme: ThemeType) => ({
  buttonWrapper: {
    ...(theme.isFriendlyUI ? {
      backgroundColor: theme.palette.primary.main,
    } : {}),
    borderTopRightRadius: theme.borderRadius.default,
    borderBottomRightRadius: theme.borderRadius.default,
    display: 'flex',
  },
  divider: {
    opacity: 0.9,
    flex: 1,
    marginTop: 'auto',
    marginBottom: 'auto',
    height: "calc(100% - 12px)",
    ...(theme.isFriendlyUI ? {
      width: 1,
      backgroundColor: theme.palette.text.alwaysWhite,
    } : {}),
  },
  button: {
    borderRadius: theme.borderRadius.default,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    padding: '6px 2px',
    minWidth: 0,
    boxShadow: 'none',
    ...(!theme.isFriendlyUI ? {
      color: theme.palette.lwTertiary.main,
      "&:hover": {
        opacity: 0.5,
      },
    } : {}),
  },
  dropdownIcon: {
    transform: 'translateY(1px)'
  },
  popper: {
    marginTop: 6
  },
  dropdownMenu: {
    ...(!theme.isFriendlyUI && {
      backgroundColor: theme.palette.dropdown.background,
      borderRadius: theme.borderRadius.small,
    }),
  },
  dropdownItem: {
    ...(!theme.isFriendlyUI && {
      padding: '4px 8px'
    })
  }
});

export const CommentsSubmitDropdown = ({ handleSubmit, classes }: {
  handleSubmit: (meta: {draft: boolean}) => Promise<void>,
  classes: ClassesType<typeof styles>,
}) => {
  const { captureEvent } = useTracking();
  const currentUser = useCurrentUser();
  const { openDialog } = useDialog();

  const [menuOpen, innerSetMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null)

  const setMenuOpen = useCallback((open: boolean) => {
    captureEvent("menuToggled", { openAfter: open, pageElementContext: "CommentsSubmitDropdown" });
    innerSetMenuOpen(open);
  }, [captureEvent, innerSetMenuOpen])

  return (
    <AnalyticsContext pageElementContext="CommentsSubmitDropdown">
      <div ref={dropdownRef} className={classes.buttonWrapper}>
        <div className={classes.divider} />
        <Button
          variant={isFriendlyUI() ? "contained" : undefined}
          color={isFriendlyUI() ? "primary" : undefined}
          className={classes.button}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <ForumIcon className={classes.dropdownIcon} icon="ThickChevronDown" />
        </Button>
      </div>
      <LWPopper
        open={menuOpen}
        anchorEl={dropdownRef.current}
        placement="bottom-end"
        className={classes.popper}
      >
        <LWClickAwayListener onClickAway={() => setMenuOpen(false)}>
          <Paper>
            <DropdownMenu className={classes.dropdownMenu}>
              <DropdownItem
                title={preferredHeadingCase("Save As Draft")}
                menuItemClassName={classes.dropdownItem}
                onClick={() => {
                  if (!currentUser) {
                    openDialog({
                      name: "LoginPopup",
                      contents: ({onClose}) => <LoginPopup onClose={onClose}/>
                    });
                    return;
                  }

                  void handleSubmit({ draft: true });
                  setMenuOpen(false);
                }}
              />
            </DropdownMenu>
          </Paper>
        </LWClickAwayListener>
      </LWPopper>
    </AnalyticsContext>
  );
}

export default registerComponent('CommentsSubmitDropdown', CommentsSubmitDropdown, {styles});
